import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import api from '../services/api';
import '../styles/escola.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';
import { useNavigate } from 'react-router-dom';

const EscolaManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_escola: '',
    id_municipio: '',
    nome_escola: '',
    sigla: '',
    endereco: '',
    complemento: '',
    bairro: '',
    geom_localizacao: '',
    censo: '',
    localizacao: '',
    status: 'Indisponível',
    georreferenciado: false,
    urn: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [municipioSearch, setMunicipioSearch] = useState('');
  const [municipios, setMunicipios] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar municípios com base na busca
  const municipiosFiltrados = useMemo(() => {
    if (!municipioSearch) return municipios;
    return municipios.filter(municipio =>
      municipio.nome_municipio?.toLowerCase().includes(municipioSearch.toLowerCase()) ||
      municipio.id_municipio?.toString().includes(municipioSearch)
    );
  }, [municipios, municipioSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchMunicipios = useCallback(async () => {
    try {
      const response = await api.get('/municipio');
      setMunicipios(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar municípios: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    // Crie uma cópia do objeto para não modificar o estado original do Tabulator
    const dadosParaEnviar = { ...dadosAtualizacao };

    // Remova campos que não existem no backend
    delete dadosParaEnviar.salvar;
    delete dadosParaEnviar.excluir;
    delete dadosParaEnviar.mapa;

    console.log('📤 Atualizando escola:', { id, dadosParaEnviar });

    try {
      const response = await api.put(`/escola/${id}`, dadosParaEnviar);
      console.log('✅ Escola atualizada:', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          // A resposta da API deve conter os dados atualizados para a linha
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      console.error('❌ Erro ao atualizar escola:', {
        id,
        mensagem: error.message,
        status: error.response?.status,
        dadosEnviados: dadosParaEnviar,
        erroBackend: error.response?.data,
        erroCompleto: error
      });

      // Mensagem de erro detalhada por tipo
      let mensagemErro = 'Erro ao atualizar registro';

      if (error.response?.status === 400) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 400 - Dados inválidos: ${backendError || 'Verifique os campos'}`;
      } else if (error.response?.status === 404) {
        mensagemErro = `Erro 404 - Escola "${id}" não encontrada.`;
      } else if (error.response?.status === 500) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 500 - Erro no servidor: ${backendError || 'Tente novamente'}`;
      } else if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      } else {
        mensagemErro = `Erro: ${error.message}`;
      }

      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    console.log('📤 Excluindo escola:', id);
    try {
      await api.delete(`/escola/${id}`);
      console.log('✅ Escola excluída:', id);
      setDados(currentData => currentData.filter(item => item.id_escola !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao excluir escola:', {
        id,
        mensagem: error.message,
        status: error.response?.status,
        erroBackend: error.response?.data,
        erroCompleto: error
      });

      // Mensagem de erro detalhada por tipo
      let mensagemErro = 'Erro ao excluir registro';

      if (error.response?.status === 404) {
        mensagemErro = `Erro 404 - Escola "${id}" não encontrada.`;
      } else if (error.response?.status === 409 || error.response?.status === 400) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro - Não é possível excluir: ${backendError || 'Escola pode estar vinculada a outros registros'}`;
      } else if (error.response?.status === 500) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 500 - Erro no servidor: ${backendError || 'Tente novamente'}`;
      } else if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      } else {
        mensagemErro = `Erro: ${error.message}`;
      }

      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const abrirMapa = useCallback((id) => {
    navigate(`/mapaEscola/${id}`);
  }, [navigate]);

  const getColunas = useMemo(() => {
    const municipiosNomes = municipios.reduce((acc, municipio) => {
      acc[municipio.id_municipio] = municipio.nome_municipio;
      return acc;
    }, {});

    const colunasBase = [
      {
        title: 'INEP',
        field: 'id_escola',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar INEP...',
        sorter: 'string',
        frozen: true,
      },
      {
        title: 'Município',
        field: 'id_municipio',
        width: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar por município...',
        sorter: 'string',
        formatter: (cell) => {
          return municipiosNomes[cell.getValue()] || cell.getValue();
        },
        editor: 'list',
        editorParams: {
          values: municipiosNomes
        }
      },
      {
        title: 'Nome da Escola',
        field: 'nome_escola',
        minWidth: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
        tooltip: (e, cell) => cell.getValue()
      },
      {
        title: 'Sigla',
        field: 'sigla',
        minWidth: 100,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar sigla...',
        sorter: 'string',
        editor: 'input',
        tooltip: (e, cell) => cell.getValue()
      },
      {
        title: 'Endereço',
        field: 'endereco',
        minWidth: 300,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar endereço...',
        sorter: 'string',
        tooltip: (e, cell) => cell.getValue()
      },
      {
        title: 'Complemento',
        field: 'complemento',
        minWidth: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar complemento...',
        sorter: 'string',
        tooltip: (e, cell) => cell.getValue()
      },
      {
        title: 'Bairro',
        field: 'bairro',
        minWidth: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar bairro...',
        sorter: 'string',
        tooltip: (e, cell) => cell.getValue()
      },
      {
        title: 'Censo',
        field: 'censo',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar censo...',
        sorter: 'string',
      },
      {
        title: 'Localização',
        field: 'localizacao',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar localização...',
        sorter: 'string',
      },
      {
        title: 'Status',
        field: 'status',
        width: 150,
        headerFilter: 'list',
        headerFilterParams: {
          values: {
            '': 'Todos',
            'Inicial': 'Inicial',
            'Ativa': 'Ativa',
            'Em tratamento': 'Em tratamento',
            'Indisponível': 'Indisponível'
          }
        },
        editor: 'list',
        editorParams: {
          values: ['Inicial', 'Ativa', 'Em tratamento', 'Indisponível']
        }
      },
      {
        title: 'Georreferenciado',
        field: 'georreferenciado',
        width: 200,
        hozAlign: 'center',
        formatter: 'tickCross',
        sorter: 'boolean',
        headerFilter: 'list',
        headerFilterParams: {
          values: {
            '': 'Todos',
            'true': '✅',
            'false': '❌'
          }
        }
      },
      {
        title: 'URN (3D)',
        field: 'urn',
        minWidth: 150,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar URN...',
        sorter: 'string',
        editor: 'input',
        tooltip: (e, cell) => cell.getValue()
      }
    ];

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_escola, data);
        },
        headerSort: false,
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_escola}"?`)) {
            excluirRegistro(data.id_escola);
          }
        },
        headerSort: false,
      },
      {
        title: 'Mapa',
        field: 'mapa',
        width: 70,
        hozAlign: 'center',
        formatter: () => '<button class="btn-mapa"><i class="fas fa-map"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          abrirMapa(data.id_escola);
        },
        headerSort: false,
      },
      ...colunasBase.map(col => {
        if (col.field === 'id_escola') {
          // INEP não é editável pois é a chave primária
          return col;
        }
        if (col.field === 'id_municipio' || col.field === 'status') {
          // Campos com editor já definido
          return col;
        }
        if (col.field === 'georreferenciado') {
          // Campos booleanos com editor checkbox
          return { ...col, editor: 'tickCross' };
        }
        if (col.field === 'localizacao') {
          // Campo com opções específicas
          return { ...col, editor: 'list', editorParams: { values: { '': 'Selecione', 'Rural': 'Rural', 'Urbana': 'Urbana', 'Indígena': 'Indígena' } } };
        }
        // Demais campos são editáveis como texto
        return { ...col, editor: 'input' };
      })
    ];
  }, [municipios, atualizarRegistro, excluirRegistro, abrirMapa]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: getColunas,
      layout: 'fitDataStretch',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      resizableColumns: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_escola'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, getColunas]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/escola');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/escola');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão ou permissão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchMunicipios();
  }, [verificarAPI, fetchMunicipios]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      inicializarTabulator();
    }
  }, [dados, getColunas, apiStatus, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_escola, id_municipio, nome_escola, sigla, endereco, complemento, bairro, geom_localizacao, censo, localizacao, status, georreferenciado, urn } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_escola) {
      camposObrigatorios.push('INEP');
      erros.push('id_escola');
    }
    if (!id_municipio) {
      camposObrigatorios.push('Município');
      erros.push('id_municipio');
    }
    if (!nome_escola) {
      camposObrigatorios.push('Nome da Escola');
      erros.push('nome_escola');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_escola,
      id_municipio,
      nome_escola,
      sigla,
      endereco,
      complemento: complemento === '' ? null : complemento,
      bairro,
      geom_localizacao: geom_localizacao === '' ? null : geom_localizacao,
      censo: censo === '' ? null : censo,
      localizacao: localizacao === '' ? null : localizacao,
      status,
      georreferenciado,
      urn: urn === '' ? null : urn
    };

    console.log('📤 Dados sendo enviados para /escola:', dataToSend);

    try {
      setLoading(true);
      const response = await api.post('/escola', dataToSend);
      console.log('✅ Escola adicionada com sucesso:', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_escola: '', id_municipio: '', nome_escola: '', sigla: '', endereco: '', complemento: '', bairro: '', geom_localizacao: '', censo: '', localizacao: '', status: 'Inicial', georreferenciado: false, urn: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao adicionar escola:', {
        mensagem: error.message,
        status: error.response?.status,
        dadosEnviados: dataToSend,
        erroBackend: error.response?.data,
        erroCompleto: error
      });

      // Mensagem de erro detalhada por tipo
      let mensagemErro = 'Erro ao adicionar registro';

      if (error.response?.status === 400) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 400 - Dados inválidos: ${backendError || 'Verifique os campos preenchidos'}`;
      } else if (error.response?.status === 404) {
        mensagemErro = `Erro 404 - Município "${id_municipio}" não encontrado. Verifique se existe.`;
      } else if (error.response?.status === 409) {
        mensagemErro = `Erro 409 - Escola com INEP "${id_escola}" já existe. Use outro código.`;
      } else if (error.response?.status === 500) {
        const backendError = error.response?.data?.message || error.response?.data?.error;
        mensagemErro = `Erro 500 - Erro no servidor: ${backendError || 'Tente novamente mais tarde'}`;
      } else if (error.response?.data?.message) {
        mensagemErro = `Erro: ${error.response.data.message}`;
      } else {
        mensagemErro = `Erro: ${error.message}`;
      }

      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    let { name, value, type, checked } = e.target;

    if (name === 'id_escola' && value.length > 8) {
      value = value.slice(0, 8);
    }

    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'escolas.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Escolas</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_escola</p>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}


      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_escola">INEP:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="number"
              id="id_escola"
              name="id_escola"
              value={formData.id_escola}
              onChange={handleInputChange}
              placeholder="Ex: 12345678"
              maxLength={8}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_escola') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_municipio">Município:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="id_municipio"
              name="id_municipio"
              value={municipioSearch}
              onChange={(e) => {
                setMunicipioSearch(e.target.value);
                const municipioEncontrado = municipios.find(mun =>
                  mun.nome_municipio === e.target.value
                );
                if (municipioEncontrado) {
                  setFormData(prev => ({ ...prev, id_municipio: municipioEncontrado.id_municipio }));
                  setCamposComErro(prev => prev.filter(c => c !== 'id_municipio'));
                } else {
                  setFormData(prev => ({ ...prev, id_municipio: '' }));
                }
              }}
              list="municipios-list"
              placeholder="Digite para buscar município..."
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_municipio') ? 'input-error' : ''}
              autoComplete="off"
            />
            <datalist id="municipios-list">
              {municipiosFiltrados.map(municipio => (
                <option key={municipio.id_municipio} value={municipio.nome_municipio} />
              ))}
            </datalist>
          </div>
          <div className="form-group">
            <label htmlFor="nome_escola">Nome da escola:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="nome_escola"
              name="nome_escola"
              value={formData.nome_escola}
              onChange={handleInputChange}
              placeholder="Ex: Escola Estadual..."
              maxLength={150}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('nome_escola') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="sigla">Sigla:</label>
            <input type="text" id="sigla" name="sigla" value={formData.sigla} onChange={handleInputChange} placeholder="Ex: EEEFM" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="endereco">Endereço:</label>
            <input type="text" id="endereco" name="endereco" value={formData.endereco} onChange={handleInputChange} placeholder="Ex: Rua A, 123" maxLength={200} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="complemento">Complemento:</label>
            <input type="text" id="complemento" name="complemento" value={formData.complemento} onChange={handleInputChange} placeholder="Ex: Sala 1" maxLength={20} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="bairro">Bairro:</label>
            <input type="text" id="bairro" name="bairro" value={formData.bairro} onChange={handleInputChange} placeholder="Ex: Centro" maxLength={100} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="censo">Censo:</label>
            <input type="number" id="censo" name="censo" value={formData.censo} onChange={handleInputChange} placeholder="Ex: 123456" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="localizacao">Localização:</label>
            <select id="localizacao" name="localizacao" value={formData.localizacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione a localização</option>
              <option value="Rural">Rural</option>
              <option value="Urbana">Urbana</option>
              <option value="Indígena">Indígena</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="status">Status:</label>
            <select id="status" name="status" value={formData.status} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="Inicial">Inicial</option>
              <option value="Ativa">Ativa</option>
              <option value="Em tratamento">Em tratamento</option>
              <option value="Indisponível">Indisponível</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="georreferenciado">Georreferenciado:</label>
            <input type="checkbox" id="georreferenciado" name="georreferenciado" checked={formData.georreferenciado} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="urn">URN (3D):</label>
            <input type="text" id="urn" name="urn" value={formData.urn} onChange={handleInputChange} placeholder="Ex: dXJuOmFkc2sub2JqZWN0c...=" maxLength={255} disabled={loading || apiStatus !== 'connected'} />
          </div>

          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>


      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Escolas com 3D (URN): <strong>{dados.filter(d => d.urn).length}</strong></div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default EscolaManager;